<template>
  <section class="about-hero-section">
    <!-- 导航栏下方的渐变线条 -->
    <div class="global-divider"></div>

    <div class="hero-container">

      <!-- 左侧视觉区 (3D画布 + 悬浮数据卡片) -->
      <div class="left-visual">
        <!-- 3D容器 -->
        <div class="three-container" ref="container"></div>

        <!-- 悬浮数据卡片层 -->
        <div class="data-cards-layer">

          <!-- 左上角状态标签 -->
          <div class="status-pill">
            <span class="pulse-dot"></span> TRACING LIVE
          </div>

          <!-- 上侧卡片 -->
          <div class="float-card card-top">
            <div class="card-label">CASES REVIEWED</div>
            <div class="big-num">5,000+</div>
            <div class="sub-label">SINCE FOUNDING</div>
            <div class="progress-bar">
              <div class="progress" style="width: 85%;"></div>
            </div>
          </div>

          <!-- 左侧卡片 -->
          <div class="float-card card-left">
            <div class="card-label">FUNDS RECOVERED</div>
            <div class="big-num">$1.83M</div>
            <div class="sub-label">AND COUNTING</div>
            <div class="progress-bar">
              <div class="progress" style="width: 70%;"></div>
            </div>
          </div>

          <!-- 右侧卡片 -->
          <div class="float-card card-right">
            <div class="card-label">SUCCESS RATE</div>
            <div class="big-num">94.7%</div>
            <div class="sub-label">CASE CLOSURE</div>
            <div class="progress-bar">
              <div class="progress" style="width: 94.7%;"></div>
            </div>
          </div>

          <!-- 左下角卡片 -->
          <div class="float-card card-bottom">
            <div class="card-label">COUNTRIES</div>
            <div class="big-num">40+</div>
            <div class="sub-label">GLOBAL REACH</div>
            <div class="progress-bar">
              <div class="progress" style="width: 40%;"></div>
            </div>
          </div>

        </div>
      </div>

      <!-- 右侧文本区 (字体小一点，间距紧凑) -->
      <div class="right-content">
        <div class="who-we-are"><span class="orange-dot"></span> WHO WE ARE</div>

        <h1 class="main-title">
          Built by<br>
          Investigators.<br>
          <span class="gradient-text">Trusted by<br>Victims.</span>
        </h1>

        <p class="description">
          Fraud Counsel Department was founded by blockchain investigators, digital forensic specialists, and cybercrime
          analysts who saw a gap the justice system couldn't close fast enough — so we built the team that could.
        </p>

        <div class="mission-card">
          <div class="mission-title">// OUR MISSION</div>
          <p class="mission-text">To restore trust and financial security in the digital economy by delivering proven
            crypto recovery solutions — protecting individuals and businesses from cyber-enabled fraud, worldwide.</p>
        </div>

        <div class="buttons">
          <button class="btn-primary">⚡ START RECOVERY</button>
          <button class="btn-secondary">🛡️ Meet the Team</button>
        </div>

        <div class="tags-container">
          <div v-for="tag in tags" :key="tag" class="tag-item">
            <span class="tag-dot"></span> {{ tag }}
          </div>
        </div>
      </div>

    </div>
  </section>
</template>

<script>
import * as THREE from 'three';

export default {
  name: 'AboutHero',
  data() {
    return {
      tags: ['BLOCKCHAIN FORENSICS', 'DIGITAL INVESTIGATIONS', 'LEGAL ENFORCEMENT', '40+ COUNTRIES', '24/7 SUPPORT'],
      scene: null,
      camera: null,
      renderer: null,
      group: null,
      centerCube: null,
      orbitingCubes: [],
      animationId: null,
      mouseX: 0,
      mouseY: 0,
      targetMouseX: 0,
      targetMouseY: 0,
      resizeHandler: null
    }
  },
  mounted() {
    // 移除原来的大屏限制，让手机端也能初始化3D
    this.initThree();
    this.animate();
    window.addEventListener('mousemove', this.onMouseMove);

    // 添加 resize 监听，确保手机横竖屏切换时渲染尺寸正确
    this.resizeHandler = () => this.onResize();
    window.addEventListener('resize', this.resizeHandler);
  },
  beforeDestroy() {
    window.removeEventListener('mousemove', this.onMouseMove);
    window.removeEventListener('resize', this.resizeHandler);
    cancelAnimationFrame(this.animationId);
    if (this.renderer) {
      this.renderer.dispose();
      if (this.renderer.domElement && this.renderer.domElement.parentNode) {
        this.renderer.domElement.parentNode.removeChild(this.renderer.domElement);
      }
    }
  },
  methods: {
    onResize() {
      const container = this.$refs.container;
      if (!container || !this.renderer) return;
      this.camera.aspect = container.clientWidth / container.clientHeight;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(container.clientWidth, container.clientHeight);
    },
    onMouseMove(event) {
      this.targetMouseX = (event.clientX / window.innerWidth) * 2 - 1;
      this.targetMouseY = -(event.clientY / window.innerHeight) * 2 + 1;
    },

    createCubeWithWire(size, color, opacity) {
      const group = new THREE.Group();
      const geometry = new THREE.BoxGeometry(size, size, size);
      const material = new THREE.MeshPhongMaterial({
        color: color,
        transparent: true,
        opacity: opacity,
        emissive: 0x000000
      });
      const mesh = new THREE.Mesh(geometry, material);
      group.add(mesh);

      // 外围线框，放大1.5倍拉开间距
      const wireSize = size * 1.5;
      const wireGeometry = new THREE.BoxGeometry(wireSize, wireSize, wireSize);
      const edges = new THREE.EdgesGeometry(wireGeometry);
      const line = new THREE.LineSegments(edges, new THREE.LineBasicMaterial({ color: 0x4fd1c5, linewidth: 1 }));
      group.add(line);

      return group;
    },

    initThree() {
      const container = this.$refs.container;
      if (!container) return;

      this.scene = new THREE.Scene();
      this.camera = new THREE.PerspectiveCamera(60, container.clientWidth / container.clientHeight, 0.1, 1000);
      this.camera.position.set(0, 0, 8);

      this.renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
      this.renderer.setSize(container.clientWidth, container.clientHeight);
      container.appendChild(this.renderer.domElement);

      const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
      this.scene.add(ambientLight);
      const dirLight = new THREE.DirectionalLight(0xffffff, 0.8);
      dirLight.position.set(5, 10, 7);
      this.scene.add(dirLight);

      this.group = new THREE.Group();

      this.centerCube = this.createCubeWithWire(1.1, 0x4fd1c5, 0.6);
      this.group.add(this.centerCube);

      const colors = [0x4fd1c5, 0xffd700, 0xff5f56, 0x00d2ff, 0xa29bfe, 0x86e59e];
      const orbitRadius = 4.0;

      for (let i = 0; i < 12; i++) {
        const cube = this.createCubeWithWire(0.4, colors[i % colors.length], 0.8);
        cube.userData.angle = (i / 12) * Math.PI * 2;
        cube.userData.orbitRadius = orbitRadius;
        cube.userData.spinSpeed = 0.5 + Math.random() * 1;
        this.orbitingCubes.push(cube);
        this.group.add(cube);
      }

      this.scene.add(this.group);
    },

    animate() {
      this.animationId = requestAnimationFrame(this.animate);
      const time = performance.now() * 0.001;

      if (this.centerCube) {
        this.centerCube.rotation.x = time;
        this.centerCube.rotation.y = time * 1.2;
      }

      this.orbitingCubes.forEach((cube) => {
        const angle = cube.userData.angle + time * 0.5;
        cube.position.x = Math.cos(angle) * cube.userData.orbitRadius;
        cube.position.z = Math.sin(angle) * cube.userData.orbitRadius;

        const rotation = Math.sin(time * cube.userData.spinSpeed) * Math.PI;
        cube.rotation.y = rotation;
        cube.rotation.x = rotation * 0.5;
      });

      this.mouseX += (this.targetMouseX - this.mouseX) * 0.05;
      this.mouseY += (this.targetMouseY - this.mouseY) * 0.05;

      if (this.group) {
        this.group.rotation.y = this.mouseX * 0.5;
        this.group.rotation.x = this.mouseY * 0.5;
        this.group.position.x = this.mouseX * 0.5;
        this.group.position.y = this.mouseY * 0.5;
      }

      this.renderer.render(this.scene, this.camera);
    }
  }
}
</script>

<style scoped>
/* 增加渐变线条容器 */
.about-hero-section {
  position: relative;
  background-color: #070b14;
  background-image: linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px);
  background-size: 50px 50px;
  padding: 80px 10%;
}

/* 导航栏下方中央向两边渐变的线条（粗线条） */
.global-divider {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 4px;
  background: linear-gradient(90deg, rgba(79, 209, 197, 0) 0%, #4fd1c5 50%, rgba(79, 209, 197, 0) 100%);
  z-index: 10;
}

.hero-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 50px;
  align-items: center;
}

/* 左侧视觉容器 */
.left-visual {
  position: relative;
  width: 100%;
  height: 550px;
}

.three-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  /* 3D层级 */
}

/* 悬浮数据卡片层 */
.data-cards-layer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 2;
  /* 卡片层级 */
}

/* 状态胶囊 */
.status-pill {
  position: absolute;
  top: 30px;
  left: 10px;
  background: #0d1117;
  border: 1px solid #4fd1c5;
  padding: 10px 20px;
  border-radius: 50px;
  color: #4fd1c5;
  font-family: monospace;
  font-weight: bold;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 10px;
}

.pulse-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #4fd1c5;
  box-shadow: 0 0 10px #4fd1c5;
  animation: pulse 1.5s infinite;
}

@keyframes pulse {

  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.2;
  }
}

/* 浮动卡片通用样式 */
.float-card {
  position: absolute;
  background: rgba(13, 17, 23, 0.9);
  border: 1px solid rgba(37, 48, 69, 0.8);
  border-radius: 8px;
  padding: 20px;
  width: 180px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
  animation: float 6s ease-in-out infinite;
  pointer-events: auto;
}

.card-top {
  top: 20px;
  right: 0;
  animation-delay: 0s;
}

.card-left {
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  animation-delay: 1s;
}

.card-right {
  right: 0;
  top: 60%;
  animation-delay: 2s;
}

.card-bottom {
  left: 20px;
  bottom: 0;
  animation-delay: 3s;
}

@keyframes float {

  0%,
  100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-10px);
  }
}

.card-label {
  color: #ffd700;
  font-size: 0.8rem;
  margin-bottom: 10px;
  letter-spacing: 1px;
}

.big-num {
  color: #4fd1c5;
  font-size: 2.2rem;
  font-weight: 800;
  margin-bottom: 5px;
}

.sub-label {
  color: #a0b3cc;
  font-size: 0.8rem;
  margin-bottom: 15px;
}

.progress-bar {
  width: 100%;
  height: 4px;
  background: #1f2937;
  border-radius: 2px;
  overflow: hidden;
}

.progress {
  height: 100%;
  background: #4fd1c5;
  border-radius: 2px;
}

/* 右侧文本区 */
.right-content {
  z-index: 4;
}

.who-we-are {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #fff;
  font-size: 0.85rem;
  font-weight: bold;
  margin-bottom: 20px;
  letter-spacing: 2px;
}

.orange-dot {
  width: 8px;
  height: 8px;
  background: #ffbd2e;
  border-radius: 50%;
}

.main-title {
  font-size: 3.5rem;
  line-height: 1.1;
  font-weight: 800;
  margin: 0 0 20px;
  color: #ffffff;
}

.gradient-text {
  background: linear-gradient(90deg, #4fd1c5, #8be9fd);
  -webkit-background-clip: text;
  color: transparent;
}

.description {
  color: #a0b3cc;
  font-size: 1rem;
  line-height: 1.6;
  margin-bottom: 25px;
  max-width: 600px;
}

.mission-card {
  background: rgba(13, 17, 23, 0.8);
  border: 1px solid #1f2937;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 25px;
}

.mission-title {
  color: #4fd1c5;
  font-family: monospace;
  font-size: 0.9rem;
  margin-bottom: 10px;
}

.mission-text {
  color: #d1d5db;
  font-size: 0.95rem;
  font-style: italic;
  line-height: 1.7;
  margin: 0;
}

.buttons {
  display: flex;
  gap: 15px;
  margin-bottom: 30px;
}

.btn-primary {
  background: linear-gradient(90deg, #86e59e, #00d68f);
  color: #000;
  border: none;
  padding: 15px 25px;
  font-size: 0.95rem;
  font-weight: bold;
  border-radius: 8px;
  cursor: pointer;
  box-shadow: 0 0 20px rgba(0, 214, 143, 0.3);
  transition: transform 0.3s;
}

.btn-primary:hover {
  transform: translateY(-3px);
}

.btn-secondary {
  background: transparent;
  border: 1px solid #2a303c;
  color: #fff;
  padding: 15px 25px;
  font-size: 0.95rem;
  font-weight: bold;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-secondary:hover {
  border-color: #4fd1c5;
  color: #4fd1c5;
}

.tags-container {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.tag-item {
  border: 1px solid #2a303c;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 0.8rem;
  font-weight: bold;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 8px;
}

.tag-dot {
  width: 6px;
  height: 6px;
  background: #4fd1c5;
  border-radius: 50%;
}

/* 手机端响应式布局：保留3D，卡片缩小，占满四周 */
@media (max-width: 900px) {
  .about-hero-section {
    padding: 50px 5%;
  }

  .global-divider {
    height: 3px;
  }

  .hero-container {
    grid-template-columns: 1fr;
    text-align: center;
    gap: 30px;
  }

  /* 【核心修改】手机端不再隐藏3D，改为绝对定位 */
  .three-container {
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
  }

  /* 【核心修改】手机端保持块级布局，撑起3D可视高度 */
  .left-visual {
    position: relative;
    width: 100%;
    height: 350px;
    /* 手机端调小高度 */
    margin-bottom: 30px;
    overflow: hidden;
  }

  /* 【核心修改】卡片层恢复绝对定位，改为四周环绕，漏出中心3D */
  .data-cards-layer {
    position: absolute;
    width: 100%;
    height: 100%;
    z-index: 2;
  }

  .status-pill {
    position: absolute;
    top: 10px;
    left: 5px;
    padding: 5px 10px;
    font-size: 0.6rem;
  }

  /* 缩小卡片尺寸，保留边缘环绕效果 */
  .float-card {
    position: absolute;
    width: 110px;
    /* 从180px缩小到110px */
    padding: 10px;
  }

  .card-top {
    top: 10px;
    right: 5px;
  }

  .card-left {
    left: 5px;
    top: 50%;
    transform: translateY(-50%);
  }

  .card-right {
    right: 5px;
    top: 50%;
    transform: translateY(-50%);
  }

  .card-bottom {
    left: 10px;
    bottom: 10px;
  }

  /* 缩小卡片内部文字比例 */
  .card-label {
    font-size: 0.6rem;
    margin-bottom: 5px;
  }

  .big-num {
    font-size: 1.5rem;
    margin-bottom: 2px;
  }

  .sub-label {
    font-size: 0.6rem;
    margin-bottom: 8px;
  }

  .who-we-are {
    justify-content: center;
  }

  .main-title {
    font-size: 2.5rem;
  }

  .description {
    margin: 0 auto 20px;
  }

  .mission-card {
    text-align: left;
  }

  .buttons {
    flex-direction: column;
    align-items: center;
  }

  .btn-primary,
  .btn-secondary {
    width: 100%;
  }

  .tags-container {
    justify-content: center;
  }
}
</style>